// src/components/Breadcrumbs/Breadcrumbs.jsx
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";

// Segmentti -> luettava nimi
const LABELS = {
  etusivu: "Etusivu",
  esiintyja: "Esiintyjä",
  esiintyjat: "Esiintyjät",
  yritys: "Yritys",
  yritykset: "Yritykset",
  blogi: "Blogi",
  useinkysyttya: "UKK",
  tietoameista: "Tietoa meistä",
  otayhteytta: "Ota yhteyttä",

  backstage: "Backstage",
  kayttoehdot: "Käyttöehdot",
  tietosuoja: "Tietosuoja",

};

// Segmentit, joita EI koskaan linkitetä (esim. tosi dynaamiset lopputasot)
const NO_LINK = new Set(["varaus"]);

// Segmentit, joille on suora sivu samalla polulla
const LINKABLE = new Set([
  "blogi",
  "useinkysyttya",
  "tietoameista",
  "otayhteytta",
  "liity-esiintyjaksi",
  "backstage",
  "kayttoehdot",
  "tietosuoja",
  "esiintyjat",
  "yritykset",
]);

// Erikoisreititys: dynaaminen yksikkö -> lista
// /esiintyja/:slug  → linkki /esiintyjat
// /yritys/:slug     → linkki /yritykset
const LINK_TO = {
  esiintyja: "/esiintyjat",
  yritys: "/yritykset",
};

const humanize = (seg) =>
  LABELS[seg] ?? seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

// Fallback-reitit JSON-LD:lle, jos items-propissa ei ole `to`-kenttää
const LABEL_TO_URL = {
  "Etusivu": "/",
  "Esiintyjä": "/esiintyjat",
  "Yritys": "/yritykset",
};

export default function Breadcrumbs({ currentLabel, items, homeLabel = "Etusivu" }) {
  const { pathname } = useLocation();

  // Absoluuttinen origin JSON-LD:n item-URL:lle
  const origin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  // Nykyisen sivun absoluuttinen URL viimeistä murua varten
  const currentUrl = origin ? new URL(pathname || "/", origin).href : (pathname || "/");

  let crumbs = items;
  if (!crumbs) {
    const parts = pathname.split("/").filter(Boolean); // esim. ["esiintyja","bilebandi-joyride"]
    const acc = [];
    crumbs = parts.map((seg, i) => {
      acc.push(seg);
      const isLast = i === parts.length - 1;
      const label = isLast && currentLabel ? currentLabel : humanize(seg);

      // linkitetään vain jos EI viimeinen ja segmentti ei ole kielletty
      let to;
      if (!isLast && !NO_LINK.has(seg)) {
        // erikoisreititys listaan vai suora kertymäpolku
        to = LINK_TO[seg] || "/" + acc.join("/");
      }

      // Lisäksi linkitetään vain segmentit, joille on oikea sivu
      // (jos erikoisreititys toi 'to':n, se kelpaa aina)
      const canLink = Boolean(to) && (LINKABLE.has(seg) || LINK_TO[seg]);

      return { label, to: canLink ? to : undefined };
    });

    crumbs = [{ label: homeLabel, to: "/" }, ...crumbs];
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => {
      const base = { "@type": "ListItem", position: i + 1, name: c.label };

      // Määritä item kaikille muruillle:
      // 1) ensisijaisesti käytä propsin `to`-arvoa
      // 2) muuten kokeile label-pohjainen fallback (Etusivu/Esiintyjä/Yritys)
      // 3) viimeinen murupala = nykyinen sivu
      let href = null;
      if (origin) {
        if (c.to) {
          href = new URL(c.to, origin).href;
        } else if (LABEL_TO_URL[c.label]) {
          href = new URL(LABEL_TO_URL[c.label], origin).href;
        } else if (i === crumbs.length - 1) {
          href = currentUrl;
        }
      }
      if (href) base.item = href;
      return base;
    }),
  };

  return (
    <nav aria-label="Murupolku" className="mb-3">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <ol className="flex flex-wrap items-center gap-1 text-sm text-black/70 dark:text-white/70">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <li key={`${c.label}-${i}`} className="inline-flex items-center gap-1">
              {!last && c.to ? (
                <Link to={c.to} className="hover:underline hover:text-black dark:hover:text-white">
                  {c.label}
                </Link>
              ) : (
                <span
                  aria-current={last ? "page" : undefined}
                  className={`font-semibold ${last ? "text-black/80 dark:text-white" : ""}`}
                >
                  {c.label}
                </span>
              )}
              {!last && <span className="px-1 text-black/35 dark:text-white/35">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

Breadcrumbs.propTypes = {
  currentLabel: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, to: PropTypes.string })
  ),
  homeLabel: PropTypes.string,
};
